import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
    // `api` is a stable module-level singleton; `setUser`/`setLoading` are stable React setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const captureSession = (data) => {
    if (data?.session_token) setToken(data.session_token);
    const { session_token, ...userOnly } = data || {};
    setUser(userOnly);
    return userOnly;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return captureSession(data);
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    return captureSession(data);
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("logout request failed (ignored)", err); }
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, refresh],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
